import { useEffect, useMemo, useState } from "react";
import { RoleShell } from "@/components/RoleShell";
import { StatusPill } from "@/components/StatusPill";
import { useAppointments } from "@/hooks/useAppointments";
import { useBranch } from "@/contexts/BranchContext";
import { api, unwrap, API_BASE } from "@/lib/api";
import {
  Banknote, Smartphone, CreditCard, Sparkles, MessageCircle, Percent, Receipt as ReceiptIcon, Gift, Award, Check, FileText, Calculator, Download, X, Undo2,
} from "lucide-react";
import { toast } from "sonner";
import { BILLING } from "@/constants/testIds";
import { useSettings } from "@/contexts/SettingsContext";
import { ReconcileModal } from "@/components/ReconcileModal";
import "@/components/membership.css";

const DISCOUNT_REASONS = ["Loyalty", "Staff", "Manager Override", "Campaign"];

export default function Billing() {
  const { activeBranchId } = useBranch();
  const { items, update } = useAppointments({ branchId: activeBranchId });
  const { settings } = useSettings();

  const pending = useMemo(() => items.filter((a) => a.status === "payment-pending"), [items]);
  const completed = useMemo(() => items.filter((a) => a.status === "completed").slice().reverse().slice(0, 6), [items]);
  const collectedToday = useMemo(() => items.filter((a) => a.status === "completed").reduce((sum, a) => sum + ((a.amount_due || 0) - (a.discount_amount || 0)), 0), [items]);
  const pendingTotal = useMemo(() => pending.reduce((s, a) => s + (a.amount_due || 0), 0), [pending]);

  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => { if (!selectedId && pending.length) setSelectedId(pending[0].id); }, [pending, selectedId]);
  const selected = items.find((a) => a.id === selectedId);

  // Billing state
  const [method, setMethod] = useState("upi");
  const [splitOn, setSplitOn] = useState(false);
  const [splitA, setSplitA] = useState({ method: "cash", amount: "" });
  const [splitB, setSplitB] = useState({ method: "upi", amount: "" });
  const [discount, setDiscount] = useState("");
  const [discountReason, setDiscountReason] = useState(DISCOUNT_REASONS[0]);
  const [tip, setTip] = useState("");
  const [useLoyalty, setUseLoyalty] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  const [giftAmount, setGiftAmount] = useState(0);
  const [client, setClient] = useState(null);
  const [membership, setMembership] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);

  useEffect(() => {
    setMethod("upi"); setSplitOn(false); setSplitA({ method: "cash", amount: "" }); setSplitB({ method: "upi", amount: "" });
    setDiscount(""); setDiscountReason(DISCOUNT_REASONS[0]); setTip(""); setUseLoyalty(false); setGiftCode(""); setGiftAmount(0);
    setMembership(null);
    setCouponCode(""); setCouponDiscount(0); setCouponError(""); setAppliedCoupon(null);
    if (selected?.client_id) {
      api.get(`/clients/${selected.client_id}`).then((r) => setClient(unwrap(r))).catch(() => setClient(null));
      api.get(`/memberships`, { params: { client_id: selected.client_id } }).then((r) => {
        const list = unwrap(r) || [];
        const m = list.find((x) => x.is_active);
        if (m) setMembership(m);
      }).catch(() => {});
    } else { setClient(null); }
  }, [selected?.id]); // eslint-disable-line

  const due = selected?.amount_due || 0;
  const loyaltyAvailable = client?.loyalty_points || 0;
  const loyaltyApplied = useLoyalty ? Math.min(loyaltyAvailable, due) : 0;
  const discountVal = Math.max(0, parseInt(discount || "0", 10));
  const tipVal = Math.max(0, parseInt(tip || "0", 10));
  const finalAmount = Math.max(1, due - discountVal - loyaltyApplied - giftAmount - couponDiscount);

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) { setCouponError("Enter a coupon code"); return; }
    setCouponError("");
    try {
      const r = await api.post(`/coupons/validate`, {
        code, amount: due, branch_id: activeBranchId,
        services: (selected?.services_requested || []),
      });
      const d = unwrap(r);
      setAppliedCoupon(d);
      setCouponDiscount(Number(d.discount_amount) || 0);
      toast.success(`${d.code} applied — ₹${d.discount_amount} off`);
    } catch (e) {
      const data = e?.response?.data?.error;
      setAppliedCoupon(null);
      setCouponDiscount(0);
      setCouponError(data?.message || "Invalid coupon code");
    }
  };

  const clearCoupon = () => {
    setCouponCode(""); setCouponDiscount(0); setCouponError(""); setAppliedCoupon(null);
  };

  const downloadInvoice = (appointmentId, invoiceNumber) => {
    const params = new URLSearchParams({
      salon_name: settings.salon_name || "",
      gstin: settings.gstin || "",
      salon_address: settings.salon_address || "",
      gst_rate: String(settings.gst_rate || 18),
    });
    const url = `${API_BASE}/reports/invoice/${appointmentId}?${params.toString()}`;
    // Fetch with auth, then open as blob (axios already attaches Bearer)
    api.get(`/reports/invoice/${appointmentId}?${params.toString()}`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = `invoice-${(invoiceNumber || appointmentId).replace(/\//g, "-")}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
      })
      .catch(() => toast.error("Couldn't download invoice"));
  };

  const applyGiftCard = async () => {
    if (!giftCode.trim()) return;
    try {
      const r = await api.get(`/gift-cards/${giftCode.trim()}`);
      const g = unwrap(r);
      if (!g) throw new Error("not found");
      const use = Math.min(g.balance, due);
      setGiftAmount(use);
      toast.success(`Gift card applied · ₹${use}`, { description: `Balance remaining ₹${g.balance - use}` });
    } catch {
      toast.error("Invalid gift card code");
    }
  };

  const confirm = async () => {
    if (!selected) return;
    if (splitOn) {
      const a = parseInt(splitA.amount || "0", 10);
      const b = parseInt(splitB.amount || "0", 10);
      if (a + b !== finalAmount) {
        toast.error(`Split must add to ₹${finalAmount}`, { description: `Currently ₹${a + b}` });
        return;
      }
    }

    setConfirming(true);
    setTimeout(() => setConfirming(false), 1100);

    try {
      // redeem gift card if used
      if (giftCode.trim() && giftAmount > 0) {
        try { await api.patch(`/gift-cards/${giftCode.trim()}/redeem`, { amount: giftAmount }); } catch {}
      }

      await update(selected.id, {
        status: "completed",
        paid: true,
        amount_due: due,
        discount_amount: discountVal + loyaltyApplied + giftAmount + couponDiscount,
        discount_reason: discountVal > 0 ? discountReason : (loyaltyApplied > 0 ? "Loyalty" : (couponDiscount > 0 ? `Coupon ${appliedCoupon?.code}` : null)),
        tip_amount: tipVal,
        loyalty_points_used: loyaltyApplied,
        gift_card_code: giftCode || null,
        gift_card_amount: giftAmount,
        coupon_code: appliedCoupon?.code || null,
        coupon_discount: couponDiscount || 0,
        payment_method: splitOn ? "split" : method,
        split_payments: splitOn ? [
          { method: splitA.method, amount: parseInt(splitA.amount || "0", 10) },
          { method: splitB.method, amount: parseInt(splitB.amount || "0", 10) },
        ] : null,
      });

      // Atomically increment coupon used_count
      if (appliedCoupon?.code) {
        try { await api.post(`/coupons/${appliedCoupon.code}/apply`, {}); } catch {}
      }

      if (selected.client_id && loyaltyApplied > 0) {
        try { await api.post(`/clients/${selected.client_id}/loyalty`, { delta: -loyaltyApplied }); } catch {}
      }

      toast.success("Payment confirmed", { description: `₹${finalAmount} · ${splitOn ? "split" : method.toUpperCase()}` });
      setShowReceipt(true);
    } catch (e) {
      toast.error("Couldn't confirm payment");
    }
  };

  return (
    <RoleShell
      title="Billing"
      showFloorMap
      rightExtras={
        <button
          onClick={() => setReconcileOpen(true)}
          className="btn-3d btn-glass-3d btn-sm"
          data-testid={BILLING.reconcileBtn}
          aria-label="Day-end reconciliation"
        >
          <Calculator size={16} /> Reconcile
        </button>
      }
    >
      <div className="max-w-[1700px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6" data-testid={BILLING.root}>
        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="glass-primary rounded-2xl p-4">
              <div className="overline text-[10px] mb-1">Pending</div>
              <div className="font-mono text-[26px] leading-none text-[var(--color-warning-deep)]">₹{pendingTotal.toLocaleString("en-IN")}</div>
              <div className="font-inter text-[11.5px] text-[var(--color-text-muted)] mt-1">{pending.length} ticket{pending.length === 1 ? "" : "s"}</div>
            </div>
            <div className="glass-primary rounded-2xl p-4">
              <div className="overline text-[10px] mb-1">Collected today</div>
              <div className="font-mono text-[26px] leading-none text-[var(--color-success-deep)]">₹{collectedToday.toLocaleString("en-IN")}</div>
              <div className="font-inter text-[11.5px] text-[var(--color-text-muted)] mt-1">{completed.length} recent</div>
            </div>
          </div>

          <div className="glass-primary rounded-[20px] p-5">
            <div className="overline mb-3">Awaiting payment</div>
            {pending.length === 0 ? (
              <div className="font-inter text-[13px] text-[var(--color-text-muted)] py-2">All clear ✨</div>
            ) : (
              <div className="space-y-2">
                {pending.map((a) => {
                  const sel = a.id === selectedId;
                  return (
                    <button key={a.id} onClick={() => setSelectedId(a.id)}
                            className={`w-full text-left rounded-xl p-3 transition border ${sel ? "bg-[var(--color-gold-mid)]/12 border-[var(--color-gold-mid)]/40" : "bg-white/55 border-white/80 hover:bg-white/80"}`}
                            data-testid={BILLING.pendingCard(a.id)}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[14px] text-[var(--color-mauve-deep)]">{a.token_number}</span>
                        <span className="font-mono text-[14px] text-[var(--color-gold-deeper)]">₹{a.amount_due || 0}</span>
                      </div>
                      <div className="font-sora font-semibold text-[14px] mt-1 truncate">{a.client_name}</div>
                      <div className="font-inter text-[11.5px] text-[var(--color-text-muted)] truncate">{a.assigned_stylist_name} · {(a.services_requested || []).join(", ")}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="glass-primary rounded-[20px] p-5">
            <div className="overline mb-3">Recent payments</div>
            {completed.length === 0 ? (
              <div className="font-inter text-[13px] text-[var(--color-text-muted)]">No payments yet.</div>
            ) : (
              <div className="space-y-1.5">
                {completed.slice(0, 5).map((a) => (
                  <div key={a.id} className="rounded-lg" style={{ background: "rgba(22,163,74,0.08)" }}>
                    <div className="flex items-center justify-between px-2.5 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {a.payment_method === "cash" && <Banknote size={14} className="text-[var(--color-success-deep)]" />}
                        {a.payment_method === "upi" && <Smartphone size={14} className="text-[var(--color-mauve)]" />}
                        {a.payment_method === "card" && <CreditCard size={14} className="text-[var(--color-gold-dark)]" />}
                        {a.payment_method === "split" && <Percent size={14} className="text-[var(--color-purple)]" />}
                        <span className="font-sora font-semibold text-[12.5px] truncate">{a.client_name}</span>
                        {a.is_refunded && (
                          <span className="status-pill" style={{ background: "rgba(185,28,28,0.12)", color: "#991B1B", padding: "2px 8px", fontSize: 9.5 }}>
                            REFUNDED
                          </span>
                        )}
                      </div>
                      <span className="font-mono text-[12.5px] text-[var(--color-success-deep)]">₹{((a.amount_due || 0) - (a.discount_amount || 0))}</span>
                    </div>
                    {!a.is_refunded && (
                      <div className="flex items-center justify-end px-2.5 pb-2 -mt-1">
                        <button
                          onClick={() => setRefundTarget(a)}
                          className="inline-flex items-center gap-1 font-inter text-[11px] font-medium text-[var(--color-danger-deep)] hover:underline"
                          data-testid={BILLING.refundLink(a.id)}
                        >
                          <Undo2 size={10} /> Refund
                        </button>
                      </div>
                    )}
                    {a.is_refunded && a.refund_amount > 0 && (
                      <div className="px-2.5 pb-2 -mt-1 font-mono text-[11px] text-[var(--color-danger-deep)] text-right">
                        Refunded ₹{a.refund_amount} ({a.refund_method?.replace("_", " ")})
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Right pane */}
        <section>
          {!selected ? (
            <div className="glass-primary rounded-[24px] p-10 text-center">
              <div className="font-display text-[22px] font-semibold mb-1">Nothing to bill ✨</div>
              <p className="font-inter text-[14px] text-[var(--color-text-muted)]">When the stylist sends a client to billing, they'll appear here.</p>
            </div>
          ) : (
            <div className="glass-primary rounded-[24px] p-7 relative overflow-visible">
              {confirming && <ParticleBurst />}

              {/* Header */}
              <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-[20px] text-[var(--color-mauve-deep)]">{selected.token_number}</span>
                    <StatusPill status={selected.status} />
                  </div>
                  <h1 className="font-display font-bold text-[30px] tracking-[-0.02em] mt-2">{selected.client_name}</h1>
                  <div className="font-inter text-[13px] text-[var(--color-text-muted)] mt-1">{selected.client_phone} · {selected.assigned_stylist_name}</div>
                </div>
                <div className="text-right">
                  <div className="overline mb-1">Amount due</div>
                  <div className="font-mono leading-none text-[var(--color-gold-deeper)]" style={{ fontSize: 52 }} data-testid={BILLING.amountDue}>
                    {settings.currency}{due}
                  </div>
                </div>
              </div>

              {/* Service notes */}
              <div className="rounded-2xl p-4 glass-subtle mb-4">
                <div className="overline mb-1.5">Stylist notes</div>
                <div className="font-inter text-[13.5px] text-[var(--color-text-secondary)]">{selected.service_notes || "—"}</div>
                {selected.formula_notes && <div className="font-mono text-[12px] text-[var(--color-text-muted)] mt-2">Formula: {selected.formula_notes}</div>}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(selected.services_rendered || selected.services_requested || []).map((s, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-full bg-[var(--color-gold-mid)]/12 text-[var(--color-gold-deeper)] font-inter font-medium text-[11.5px] border border-[var(--color-gold-mid)]/30">
                      {typeof s === "string" ? s : `${s.service_name} · ₹${s.price}`}
                    </span>
                  ))}
                </div>
              </div>

              {/* Membership card */}
              {membership && (
                <div className="rounded-2xl p-4 mb-4" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.30)" }} data-testid="billing-membership-card">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Award size={16} className="text-[var(--color-warning-deep)]" />
                    <span className="font-sora font-semibold text-[14px] text-[var(--color-warning-deep)]">{membership.plan_name}</span>
                    {membership.color_scheme && (
                      <span className={`tier-chip tier-chip-${membership.color_scheme}`}>{membership.badge_label}</span>
                    )}
                  </div>
                  <div className="font-inter text-[12.5px] text-[var(--color-warning-deep)]">
                    {membership.total_sessions ? `${membership.total_sessions - membership.used_sessions} of ${membership.total_sessions} sessions remaining` : `Active until ${membership.valid_until}`}
                    {membership.discount_percent ? ` · Auto-applied ${membership.discount_percent}% service discount: -₹${Math.round(due * membership.discount_percent / 100)}` : ""}
                    {membership.loyalty_multiplier > 1 ? ` · ${membership.loyalty_multiplier}× loyalty points on this visit` : ""}
                  </div>
                </div>
              )}

              {/* Loyalty + Gift card */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {client && loyaltyAvailable > 0 && (
                  <div className="rounded-2xl p-4 border border-white/80 bg-white/55">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles size={14} className="text-[var(--color-mauve)]" />
                      <span className="overline">Loyalty</span>
                    </div>
                    <div className="font-inter text-[13.5px]">{client.name?.split(" ")[0]} has <span className="font-mono">{loyaltyAvailable}</span> pts = <span className="font-mono">{settings.currency}{loyaltyAvailable}</span></div>
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input type="checkbox" checked={useLoyalty} onChange={(e) => setUseLoyalty(e.target.checked)} data-testid={BILLING.applyLoyaltyToggle} />
                      <span className="font-inter text-[13px]">Apply ₹{Math.min(loyaltyAvailable, due)}</span>
                    </label>
                  </div>
                )}

                <div className="rounded-2xl p-4 border border-white/80 bg-white/55">
                  <div className="flex items-center gap-2 mb-2">
                    <Gift size={14} className="text-[var(--color-gold-dark)]" />
                    <span className="overline">Gift card</span>
                  </div>
                  <div className="flex gap-2">
                    <input value={giftCode} onChange={(e) => setGiftCode(e.target.value)} placeholder="e.g. GLOSS100"
                           className="flex-1 h-10 px-3 rounded-lg bg-white border border-white/80 font-mono text-[13px] outline-none uppercase"
                           data-testid={BILLING.giftCardInput} />
                    <button className="btn-3d btn-glass-3d btn-sm" onClick={applyGiftCard}>Apply</button>
                  </div>
                  {giftAmount > 0 && <div className="mt-2 font-mono text-[12.5px] text-[var(--color-gold-deeper)]">₹{giftAmount} applied</div>}
                </div>
              </div>

              {/* Coupon code */}
              <div className="rounded-2xl p-4 border mb-4" style={{ background: "rgba(139,92,246,0.06)", borderColor: "rgba(139,92,246,0.25)" }}>
                <div className="flex items-center gap-2 mb-2">
                  <Percent size={14} className="text-[var(--color-purple)]" />
                  <span className="overline" style={{ color: "#5B21B6" }}>Coupon code</span>
                </div>
                {!appliedCoupon ? (
                  <>
                    <div className="flex gap-2">
                      <input
                        value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(""); }}
                        onKeyDown={(e) => { if (e.key === "Enter") applyCoupon(); }}
                        placeholder="e.g. WELCOME20"
                        className="flex-1 h-10 px-3 rounded-lg bg-white border border-white/80 font-mono text-[13px] outline-none uppercase"
                        data-testid={BILLING.couponInput}
                      />
                      <button className="btn-3d btn-mauve-3d btn-sm" onClick={applyCoupon} data-testid={BILLING.couponApplyBtn}>Apply</button>
                    </div>
                    {couponError && (
                      <div className="mt-2 font-inter text-[12.5px] text-[var(--color-danger-deep)]" data-testid={BILLING.couponError}>
                        {couponError}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-between gap-2" data-testid={BILLING.couponApplied}>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: "rgba(22,163,74,0.10)", border: "1px solid rgba(22,163,74,0.30)" }}>
                      <Check size={13} className="text-[var(--color-success-deep)]" />
                      <span className="font-mono text-[12.5px] text-[var(--color-success-deep)]">
                        {appliedCoupon.code} applied — ₹{appliedCoupon.discount_amount} off
                      </span>
                    </div>
                    <button
                      onClick={clearCoupon}
                      className="w-7 h-7 rounded-full glass-subtle flex items-center justify-center hover:bg-white/80"
                      data-testid={BILLING.couponClearBtn}
                      aria-label="Remove coupon"
                    >
                      <X size={13} />
                    </button>
                  </div>
                )}
              </div>

              {/* Payment methods */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <MethodTile icon={Banknote} label="Cash" active={!splitOn && method === "cash"} onClick={() => { setMethod("cash"); setSplitOn(false); }} testid={BILLING.payCashBtn} />
                <MethodTile icon={Smartphone} label="UPI"   active={!splitOn && method === "upi"}  onClick={() => { setMethod("upi"); setSplitOn(false); }} testid={BILLING.payUpiBtn} />
                <MethodTile icon={CreditCard} label="Card" active={!splitOn && method === "card"} onClick={() => { setMethod("card"); setSplitOn(false); }} testid={BILLING.payCardBtn} />
              </div>

              {settings.split_billing_enabled && (
                <div className="rounded-2xl p-4 border border-white/80 bg-white/55 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer mb-2">
                    <input type="checkbox" checked={splitOn} onChange={(e) => setSplitOn(e.target.checked)} data-testid={BILLING.splitToggle} />
                    <span className="overline">Split payment</span>
                  </label>
                  {splitOn && (
                    <div className="grid grid-cols-2 gap-2">
                      <SplitField val={splitA} onChange={setSplitA} />
                      <SplitField val={splitB} onChange={setSplitB} />
                    </div>
                  )}
                </div>
              )}

              {/* Discount */}
              <div className="rounded-2xl p-4 border mb-4" style={{ background: "rgba(124,58,237,0.05)", borderColor: "rgba(124,58,237,0.25)" }}>
                <div className="flex items-center gap-2 mb-2">
                  <Percent size={14} className="text-[var(--color-purple)]" />
                  <span className="overline" style={{ color: "#5B21B6" }}>Discount</span>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <input value={discount} onChange={(e) => setDiscount(e.target.value.replace(/\D/g, ""))} placeholder="₹0"
                         className="w-28 h-10 px-3 rounded-lg bg-white border border-white/80 font-mono text-[14px] outline-none"
                         data-testid={BILLING.discountInput} />
                  {[10, 20, 50].map((p) => (
                    <button key={p} onClick={() => setDiscount(String(Math.round((due * p) / 100)))}
                            className="px-2.5 py-1 rounded-full text-[11.5px] font-inter font-medium border border-[var(--color-purple)]/30 text-[var(--color-purple)] bg-white/60">
                      {p}%
                    </button>
                  ))}
                  <select value={discountReason} onChange={(e) => setDiscountReason(e.target.value)}
                          className="h-10 px-2.5 rounded-lg bg-white border border-white/80 font-inter text-[12.5px] outline-none">
                    {DISCOUNT_REASONS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                  <button className="ml-auto font-inter text-[12px] text-[var(--color-text-muted)]" onClick={() => setDiscount("")}>Clear</button>
                </div>
              </div>

              {/* Tip */}
              {settings.tip_enabled && (
                <div className="rounded-2xl p-4 border border-white/80 bg-white/55 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={14} className="text-[var(--color-gold-dark)]" />
                    <span className="overline">Tip</span>
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    <input value={tip} onChange={(e) => setTip(e.target.value.replace(/\D/g, ""))} placeholder="₹0"
                           className="w-28 h-10 px-3 rounded-lg bg-white border border-white/80 font-mono text-[14px] outline-none"
                           data-testid={BILLING.tipInput} />
                    {[50, 100, 200].map((p) => (
                      <button key={p} onClick={() => setTip(String(p))}
                              className="px-2.5 py-1 rounded-full text-[11.5px] font-inter font-medium border border-[var(--color-gold-mid)]/30 text-[var(--color-gold-deeper)] bg-white/60">
                        ₹{p}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Final summary */}
              <div className="flex items-center justify-between glass-subtle rounded-2xl p-4 mb-4">
                <div className="font-inter text-[13.5px] text-[var(--color-text-secondary)]">
                  <div>Subtotal: ₹{due}{discountVal > 0 && <> · disc ₹{discountVal}</>}{loyaltyApplied > 0 && <> · loyalty ₹{loyaltyApplied}</>}{giftAmount > 0 && <> · gift ₹{giftAmount}</>}{couponDiscount > 0 && <> · coupon ₹{couponDiscount}</>}{tipVal > 0 && <> · tip ₹{tipVal}</>}</div>
                </div>
                <div className="text-right">
                  <div className="overline">Client pays</div>
                  <div className="font-mono text-[28px] leading-none text-[var(--color-gold-deeper)]">₹{finalAmount + tipVal}</div>
                </div>
              </div>

              <button onClick={confirm} className="btn-3d btn-success-3d btn-xl w-full relative" data-testid={BILLING.confirmBtn}>
                <Check size={18} /> Confirm payment · ₹{finalAmount + tipVal}
              </button>

              {showReceipt && (
                <div className="mt-4 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap" style={{ background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.30)" }}>
                  <div className="flex items-center gap-2.5">
                    <FileText size={16} className="text-[var(--color-success-deep)]" />
                    <div>
                      <div className="font-sora font-semibold text-[13.5px] text-[var(--color-success-deep)]">Invoice {selected.invoice_number || "generated"}</div>
                      <div className="font-inter text-[12px] text-[var(--color-success-deep)]/80">Sent via WhatsApp · simulated for demo</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => downloadInvoice(selected.id, selected.invoice_number)}
                      className="btn-3d btn-glass-3d btn-sm"
                      data-testid={BILLING.invoiceDownloadBtn}
                    >
                      <Download size={14} /> Download Invoice
                    </button>
                    <button onClick={() => {
                      toast.success("WhatsApp receipt sent", { description: selected.client_phone });
                      api.post("/notifications/log", {
                        type: "receipt",
                        client_id: selected.client_id,
                        client_name: selected.client_name,
                        client_phone: selected.client_phone,
                        message: `Invoice ${selected.invoice_number} · ₹${finalAmount + tipVal}`,
                        amount: finalAmount + tipVal,
                        channel: "whatsapp", status: "mocked",
                        branch_id: activeBranchId,
                      }).catch(() => {});
                    }}
                            className="btn-3d btn-success-3d btn-sm" data-testid={BILLING.whatsappReceiptBtn}>
                      <MessageCircle size={14} /> Send receipt on WhatsApp
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
      {reconcileOpen && <ReconcileModal onClose={() => setReconcileOpen(false)} />}
      {refundTarget && (
        <RefundModal
          appointment={refundTarget}
          onClose={() => setRefundTarget(null)}
          onSubmitted={async () => { setRefundTarget(null); }}
        />
      )}
    </RoleShell>
  );
}

function MethodTile({ icon: Icon, label, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`rounded-2xl p-5 transition border ${active ? "bg-[var(--color-mauve)]/10 border-[var(--color-mauve)]/40 ring-2 ring-[var(--color-mauve)]/25" : "bg-white/65 border-white/80 hover:bg-white/85"}`}>
      <Icon size={24} className={active ? "text-[var(--color-mauve-deep)]" : "text-[var(--color-text-secondary)]"} />
      <div className={`mt-3 font-sora font-semibold text-[14px] ${active ? "text-[var(--color-mauve-deep)]" : "text-[var(--color-text-primary)]"}`}>{label}</div>
    </button>
  );
}

function SplitField({ val, onChange }) {
  return (
    <div className="flex gap-2">
      <select value={val.method} onChange={(e) => onChange({ ...val, method: e.target.value })}
              className="h-10 px-2 rounded-lg bg-white border border-white/80 font-inter text-[12.5px] outline-none w-24">
        <option value="cash">Cash</option>
        <option value="upi">UPI</option>
        <option value="card">Card</option>
      </select>
      <input value={val.amount} onChange={(e) => onChange({ ...val, amount: e.target.value.replace(/\D/g, "") })} placeholder="₹"
             className="flex-1 h-10 px-3 rounded-lg bg-white border border-white/80 font-mono text-[13px] outline-none" />
    </div>
  );
}

function ParticleBurst() {
  const angles = Array.from({ length: 12 }).map((_, i) => i * 30);
  return (
    <div className="particle-burst">
      {angles.map((a) => (
        <span key={a} className="particle" style={{ ["--a"]: `${a}deg` }} />
      ))}
    </div>
  );
}


// =========================
// Refund Modal (460px)
// =========================
function RefundModal({ appointment: a, onClose, onSubmitted }) {
  const net = (a.amount_due || 0) - (a.discount_amount || 0);
  const [amount, setAmount] = useState(String(net));
  const [reason, setReason] = useState("");
  const [method, setMethod] = useState(a.payment_method === "split" ? "cash" : (a.payment_method || "cash"));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const amountNum = Math.max(0, parseInt(amount || "0", 10));
  const isPartial = amountNum > 0 && amountNum < net;
  const reasonOk = (reason || "").trim().length >= 10;
  const submit = async () => {
    if (!amountNum) { toast.error("Refund amount is required"); return; }
    if (amountNum > net) { toast.error(`Amount cannot exceed ₹${net}`); return; }
    if (!reasonOk) { toast.error("Please provide a reason (min 10 characters)"); return; }
    setBusy(true);
    try {
      await api.post(`/appointments/${a.id}/refund`, {
        amount: amountNum, reason: reason.trim(), method,
      });
      toast.success(`Refund of ₹${amountNum} processed for ${a.client_name}`);
      await onSubmitted?.();
    } catch (e) {
      const data = e?.response?.data?.error;
      toast.error(data?.message || "Refund failed", { description: data?.code || "" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center anim-fade-in" style={{ background: "rgba(28,25,23,0.32)", backdropFilter: "blur(12px)" }} data-testid={BILLING.refundModal}>
      <div className="glass-elevated rounded-[24px] p-6 w-[min(460px,calc(100vw-32px))] anim-slide-up-lg space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline mb-1" style={{ color: "#B91C1C" }}>Process refund</div>
            <h2 className="font-display text-[22px] font-bold tracking-[-0.02em]">{a.client_name}</h2>
            <p className="font-inter text-[12.5px] text-[var(--color-text-muted)] mt-1">{a.token_number} · Original ₹{net} · {a.payment_method?.toUpperCase()}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>

        <div>
          <div className="overline mb-1.5">Refund amount</div>
          <div className="flex items-center h-12 rounded-xl bg-white/70 border border-white/80 px-3">
            <span className="font-mono text-[18px] text-[var(--color-text-muted)] mr-2">₹</span>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
              className="flex-1 bg-transparent font-mono text-[22px] outline-none"
              data-testid={BILLING.refundAmountInput}
            />
            <button
              onClick={() => setAmount(String(net))}
              className="font-inter text-[11.5px] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            >
              Full
            </button>
          </div>
          {isPartial && (
            <div className="mt-1 font-inter text-[11.5px] text-[var(--color-text-muted)]">
              Partial refund — original was ₹{net}
            </div>
          )}
        </div>

        <div>
          <div className="overline mb-1.5">Reason (required)</div>
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Service unsatisfactory, billing error, double charge…"
            className="w-full px-3 py-2.5 rounded-xl bg-white/70 border border-white/80 font-inter text-[13.5px] outline-none"
            data-testid={BILLING.refundReasonInput}
          />
          <div className={`mt-1 font-inter text-[11.5px] ${reasonOk ? "text-[var(--color-success-deep)]" : "text-[var(--color-text-muted)]"}`}>
            {reason.trim().length}/10 characters minimum
          </div>
        </div>

        <div>
          <div className="overline mb-1.5">Refund method</div>
          <div className="grid grid-cols-4 gap-1.5">
            {[
              { k: "cash", l: "Cash", id: "cash" },
              { k: "upi", l: "UPI", id: "upi" },
              { k: "card", l: "Card", id: "card" },
              { k: "loyalty_points", l: "Loyalty", id: "loyalty" },
            ].map((m) => (
              <button
                key={m.k}
                type="button"
                onClick={() => setMethod(m.k)}
                className={`h-10 rounded-full text-[12.5px] font-inter font-medium border transition ${method === m.k ? "bg-[var(--color-mauve)]/12 border-[var(--color-mauve)]/40 text-[var(--color-mauve-deep)]" : "bg-white/70 border-white/80 text-[var(--color-text-secondary)]"}`}
                data-testid={BILLING.refundMethod(m.id)}
              >
                {m.l}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl p-3" style={{ background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.30)" }}>
          <div className="font-inter text-[12px] text-[var(--color-warning-deep)]">
            This action cannot be undone. Ensure cash/UPI/card reversal is processed manually.
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-3d btn-glass-3d btn-sm" onClick={onClose}>Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !reasonOk || !amountNum}
            className="btn-3d btn-danger-3d btn-lg w-full"
            data-testid={BILLING.refundConfirmBtn}
          >
            <Undo2 size={14} /> Process Refund ₹{amountNum || 0}
          </button>
        </div>
      </div>
    </div>
  );
}
